import React from 'react';
import { Text, TextButton } from '@oneaudi/unified-web-components';
import { SYS_BREAKPOINT_MD } from '@oneaudi/unified-web-common';
import { useI18n } from '@oneaudi/i18n-context';
import { ACTIONS, SELECTORS, StockcarsStoreClass } from '@oneaudi/stck-store';
import { useDispatch } from 'react-redux';
import {
  useServicesContext,
  useSvd,
  generateFilterRequestParam,
  fetchResults,
  useDesktopOrMobileView,
} from '@oneaudi/vtp-shared';
import { AppContextForThisInstance, getAppContentConfiguration } from '../../hooks/tileHook';
import { StyledText, StyledTextButton } from './ZeroResultsPage.styles';

interface ZeroResultsProps {
  context: AppContextForThisInstance;
}

const ZeroResultsPage: React.FC<ZeroResultsProps> = ({ context }: ZeroResultsProps) => {
  const { newSearchUrl, vtpConfiguration } = getAppContentConfiguration();
  const zeroResultsHeadline = useI18n({
    id: 'nemo.ui.sc.result.noresultstext',
    defaultMessage: '',
  });
  const resetFiltersLabel = useI18n({
    id: 'nemo.ui.sc.filter-options.reset.label',
    defaultMessage: '',
  });
  const zeroFavsHeadline = useI18n({
    id: 'nemo.ui.sc.favorites.empty.headline',
    defaultMessage: '',
  });

  const zeroFavsInfo = useI18n({
    id: 'nemo.ui.sc.favorites.empty.copy',
    defaultMessage: '',
  });
  const newSearchLabel = useI18n({
    id: 'nemo.ui.sc.favorites.empty.newsearch',
    defaultMessage: '',
  });
  const dispatch = useDispatch();
  const { getEnvironmentConfig } = useServicesContext();
  const svd = useSvd();

  const store = StockcarsStoreClass.getInstance('', '');
  const presets = SELECTORS.FILTERS.getPersistedPresetsMap(store.state);
  const sorting = SELECTORS.SORTING.getSortingStateRaw(store.state);

  const requestParam = generateFilterRequestParam(new Map(), presets);
  requestParam.set('size', 12);
  requestParam.set('sort', sorting.results);
  requestParam.set('svd', svd);

  const clearAllFilters = async () => {
    const response = await fetchResults(
      getEnvironmentConfig()!.scs.baseUrl,
      vtpConfiguration.fields.scs_scsMarketPath,
      requestParam
    );
    dispatch(ACTIONS.FILTERS.handleFilterResponse({ response }));
    // clean the legacy filter chips
    document.querySelector('.sc-md-filter-options')?.remove();
  };
  const isMobile: boolean = useDesktopOrMobileView(SYS_BREAKPOINT_MD) === 'mobile';
  const headline = context === 'favorites' ? zeroFavsHeadline : zeroResultsHeadline;

  return (
    <>
      <StyledText>
        <Text
          tag="p"
          variant={{ order: '2', style: 'normal', type: 'copy' }}
          data-testid="zeroResultsHeadline"
        >
          {headline}
        </Text>
      </StyledText>
      {context === 'results' && (
        <StyledTextButton>
          <TextButton
            variant="primary"
            size="md"
            onClick={clearAllFilters}
            data-testid="reset"
            isFullWidth
          >
            {resetFiltersLabel}
          </TextButton>
        </StyledTextButton>
      )}
      {context === 'favorites' && (
        <>
          <StyledText>
            <Text
              tag="p"
              variant={{ order: '2', style: 'normal', type: 'copy' }}
              data-testid="zeroFavsHeadline"
            >
              {zeroFavsInfo}
            </Text>
          </StyledText>
          <StyledTextButton>
            <TextButton variant="primary" href={newSearchUrl} size="md" isFullWidth={isMobile}>
              {newSearchLabel}
            </TextButton>
          </StyledTextButton>
        </>
      )}
    </>
  );
};

export default ZeroResultsPage;
